import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fehlerText } from '../lib/belegKarte';
import {
  GutschriftAuswahl, GutschriftPosition, centText, gutschriftBerechnen, gutschriftPositionCent,
  gutschriftPositionen,
} from '../lib/rechnungsart';
import './RechnungsartDialoge.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  /** Die zu korrigierende Rechnung (festgeschrieben). */
  bezug: { id: number; nummer: string };
  /** Gutschrift-Entwurf wurde angelegt. */
  onErstellt: (meldung: string) => void;
  onAbbrechen: () => void;
}

/** Dialog „Gutschrift erstellen“: Positionen (und Teilmengen) der Bezugsrechnung wählen, Grund angeben.
    Legt einen Gutschrift-ENTWURF an (POST …/ausgangsrechnungen, rechnungsart GUTSCHRIFT); sie mindert
    den offenen Posten erst beim Festschreiben. Positionen kommen aus der Beleg-Ansicht der Bezugsrechnung. */
const GutschriftDialog: React.FC<Props> = ({ bezug, onErstellt, onAbbrechen }) => {
  const [positionen, setPositionen] = useState<GutschriftPosition[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<Record<number, GutschriftAuswahl>>({});
  const [grund, setGrund] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    axios.get(`${API_URL}/api/dokumente/ausgangsrechnungen/${bezug.id}/beleg-ansicht`)
      .then(r => { if (aktiv) setPositionen(gutschriftPositionen(r.data)); })
      .catch((err: unknown) => {
        if (!aktiv) return;
        const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
        setLadeFehler(fehlerText(detail, 'Die Positionen der Rechnung konnten nicht geladen werden.'));
      });
    return () => { aktiv = false; };
  }, [bezug.id]);

  const berechnung = useMemo(
    () => gutschriftBerechnen(positionen ?? [], auswahl), [positionen, auswahl]);

  const setzeAuswahl = (index: number, teil: Partial<GutschriftAuswahl>) =>
    setAuswahl(a => {
      const bisher: GutschriftAuswahl = a[index] ?? { gewaehlt: false, menge: '' };
      return { ...a, [index]: { ...bisher, ...teil } };
    });

  const alleWaehlen = () => {
    const alle = (positionen ?? []).every(p => auswahl[p.index]?.gewaehlt);
    const neu: Record<number, GutschriftAuswahl> = {};
    (positionen ?? []).forEach(p => { neu[p.index] = { gewaehlt: !alle, menge: '' }; });
    setAuswahl(neu);
  };

  const erstellen = async () => {
    setFehler(null);
    if (berechnung.positionen.length === 0) {
      setFehler('Bitte mindestens eine Position für die Gutschrift auswählen.');
      return;
    }
    if (berechnung.ungueltig.length > 0) {
      setFehler('Bitte die Menge prüfen: sie muss größer 0 und höchstens die Menge der Rechnung sein.');
      return;
    }
    if (!grund.trim()) {
      setFehler('Bitte einen Grund für die Gutschrift angeben.');
      return;
    }
    setLaedt(true);
    try {
      const res = await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen`, {
        rechnungsart: 'GUTSCHRIFT',
        bezug_rechnung_id: bezug.id,
        gutschrift_positionen: berechnung.positionen,
        grund: grund.trim(),
      });
      onErstellt(`Gutschrift zu ${bezug.nummer} als Entwurf angelegt (${res.data?.nummer_anzeige ?? 'Entwurf'}). `
        + 'Sie mindert den offenen Posten, sobald sie festgeschrieben ist.');
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setFehler(fehlerText(detail, 'Gutschrift konnte nicht erstellt werden.'));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div className="rd-dialog" role="dialog" aria-label={`Gutschrift zu ${bezug.nummer}`}>
      <h4 className="rd-titel">Gutschrift zu {bezug.nummer} erstellen</h4>
      <div className="rd-hinweis">
        Wählen Sie die Positionen, die gutgeschrieben werden. Ohne Mengenangabe wird die ganze noch nicht
        gutgeschriebene Menge übernommen; bereits gutgeschriebene Mengen berücksichtigt das System.
        Die Gutschrift entsteht als Entwurf und mindert den offenen Posten erst beim Festschreiben.
      </div>

      {ladeFehler && <div className="rd-fehler" role="alert">{ladeFehler}</div>}
      {!ladeFehler && positionen === null && <div className="rd-hinweis">Positionen werden geladen…</div>}
      {positionen !== null && positionen.length === 0 && (
        <div className="rd-hinweis">Die Rechnung hat keine gutschreibbaren Positionen.</div>
      )}

      {positionen !== null && positionen.length > 0 && (
        <table className="rd-tabelle">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Alle Positionen wählen"
                  checked={positionen.every(p => auswahl[p.index]?.gewaehlt)}
                  onChange={alleWaehlen}
                />
              </th>
              <th>Position</th>
              <th className="num">Menge laut Rechnung</th>
              <th className="num">Gutzuschreibende Menge</th>
              <th className="num">Betrag netto</th>
            </tr>
          </thead>
          <tbody>
            {positionen.map(p => {
              const a = auswahl[p.index];
              const cent = a?.gewaehlt ? gutschriftPositionCent(p, a.menge) : null;
              const ungueltig = !!a?.gewaehlt && cent === null;
              return (
                <tr key={p.index}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Position ${p.index + 1} gutschreiben`}
                      checked={!!a?.gewaehlt}
                      onChange={e => setzeAuswahl(p.index, { gewaehlt: e.target.checked })}
                    />
                  </td>
                  <td>{p.index + 1}. {p.bezeichnung}</td>
                  <td className="num">{p.mengeText} {p.einheit}</td>
                  <td className="num">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="rd-menge"
                      aria-label={`Menge Position ${p.index + 1}`}
                      placeholder="alle"
                      disabled={!a?.gewaehlt}
                      value={a?.menge ?? ''}
                      onChange={e => setzeAuswahl(p.index, { menge: e.target.value })}
                      style={ungueltig ? { borderColor: 'var(--red)' } : undefined}
                    />
                  </td>
                  <td className="num">
                    {a?.gewaehlt
                      ? (cent !== null ? centText(cent) : '–')
                      : (p.gesamtCent !== null ? <span className="rd-hinweis">{centText(p.gesamtCent)}</span> : '–')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {berechnung.positionen.length > 0 && (
        <div className="rd-vorschau" aria-live="polite">
          <div className="rd-vorschau__zeile rd-vorschau__zeile--summe">
            <span>Gutschrift netto (Vorschau)</span>
            <span>−{centText(berechnung.nettoCent)}</span>
          </div>
          <div className="rd-hinweis">
            Die Umsatzsteuer wird wie in der Ursprungsrechnung ausgewiesen und im Entwurf berechnet.
          </div>
        </div>
      )}

      <label className="rd-hinweis" htmlFor={`gutschrift-grund-${bezug.id}`}>Grund (Pflicht, wird im Protokoll festgehalten)</label>
      <textarea
        id={`gutschrift-grund-${bezug.id}`}
        className="rd-textarea"
        value={grund}
        onChange={e => setGrund(e.target.value)}
        placeholder="z. B. Mangel an Position 2, Preisnachlass vereinbart"
      />

      {fehler && <div className="rd-fehler" role="alert">{fehler}</div>}
      <div className="rd-zeile">
        <button type="button" className="k-btn k-btn--primary k-btn--sm" onClick={erstellen}
                disabled={laedt || positionen === null || positionen.length === 0}>
          {laedt ? 'Erstellt…' : 'Gutschrift als Entwurf anlegen'}
        </button>
        <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={onAbbrechen} disabled={laedt}>
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default GutschriftDialog;
